import React, { useCallback, useEffect, useState } from 'react'
import {
  DataTable,
  InlineLoading,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { loadIngestJobs, selectIngestResult, selectStatusIngest, selectToken } from '../../../AppSlice';
import Icon, { Icons } from '../../Icon/Icon';
import CreateIngestModal from './CreateIngestModal';
import { IIngestJobStatus } from './Ingest.model';

const headers = [
  { key: 'relativePath', header: 'File' },
  { key: 'status', header: 'Status' },
  { key: 'resourceId', header: 'Resource' },
  { key: 'avidExecutionId', header: 'Avid Execution' },
  { key: 'exitDescription', header: 'Result' },
  { key: 'createdAt', header: 'Created At' },
  { key: 'updatedAt', header: 'Updated At' },
];

const statuses: Array<'All' | IIngestJobStatus> = [
  'All',
  'PENDING',
  'SUBMITTING',
  'RUNNING',
  'COMPLETED',
  'FAILED',
  'START_FAILED',
  'UNKNOWN',
];

interface IActions {
  [key: string]: {
    title: string;
    icon: keyof typeof Icons;
  }
}

const actions: IActions = {
  new: { title: 'New Avid Ingest', icon: 'newFolder' },
  refresh: { title: 'Refresh', icon: 'refresh' },
}

const getStatusClass = (status: string): string => {
  switch (status?.toUpperCase()) {
    case 'COMPLETED':
      return 'progress-success';
    case 'FAILED':
    case 'START_FAILED':
      return 'progress-danger';
    case 'SUBMITTING':
    case 'RUNNING':
      return 'progress-info';
    default:
      return 'progress-default';
  }
}

const displayStatus = (status: string) => status?.replace(/_/g, ' ') ?? '—'

function Ingest() {
  const dispatch = useAppDispatch()
  const token = useAppSelector(selectToken)
  const ingestResult = useAppSelector(selectIngestResult)
  const ingestStatus = useAppSelector(selectStatusIngest)

  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [filterStatus, setFilterStatus] = useState<'All' | IIngestJobStatus>('All')
  const [createModalOpen, setCreateModalOpen] = useState(false)

  const loadIngests = useCallback(() => {
    if (token) dispatch(loadIngestJobs({ token }))
  }, [dispatch, token])

  useEffect(() => {
    loadIngests()
  }, [loadIngests])

  const content = ingestResult?.content ?? []
  const hasActiveJobs = content.some(job =>
    ['PENDING', 'SUBMITTING', 'RUNNING'].includes(job.status?.toUpperCase())
  )

  useEffect(() => {
    if (!token || !hasActiveJobs) return
    const interval = window.setInterval(() => dispatch(loadIngestJobs({ token })), 5000)
    return () => window.clearInterval(interval)
  }, [dispatch, hasActiveJobs, token])

  const filteredJobs = filterStatus === 'All'
    ? content
    : content.filter(job => job.status?.toUpperCase() === filterStatus)

  const firstRow = (currentPage - 1) * rowsPerPage
  const rows = filteredJobs
    .slice(firstRow, firstRow + rowsPerPage)
    .map(job => ({
      id: job.id,
      relativePath: job.relativePath,
      status: job.status,
      resourceId: job.resourceId,
      avidExecutionId: job.avidExecutionId ?? '—',
      exitDescription: job.exitDescription ?? (job.exitCode === null ? '—' : `Exit code ${job.exitCode}`),
      createdAt: job.createdAt,
      updatedAt: job.updatedAt,
    }))

  const onActionClick = (action: string) => {
    if (action === 'new') setCreateModalOpen(true)
    if (action === 'refresh') loadIngests()
  }

  return (
    <>
      <TableContainer>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="status-filters">
            {Object.keys(actions).map(action => (
              <div
                key={action}
                className={`action-box action-${action}`}
                onClick={() => onActionClick(action)}
              >
                <Icon type={actions[action].icon} color="white" size={16} />
                <div style={{ fontSize: 13, marginTop: 2 }}>{actions[action].title}</div>
              </div>
            ))}
            <div style={{ width: 20 }} />
            {statuses.map(status => (
              <div
                key={status}
                className={`status-box ${getStatusClass(status)} ${filterStatus === status ? 'active' : ''}`}
                style={{ borderWidth: filterStatus === status ? 3 : 1, cursor: 'pointer' }}
                onClick={() => {
                  setFilterStatus(status)
                  setCurrentPage(1)
                }}
              >
                <div>{displayStatus(status)}</div>
                <div style={{ marginTop: 4 }}>
                  {status === 'All'
                    ? content.length
                    : content.filter(job => job.status?.toUpperCase() === status).length}
                </div>
              </div>
            ))}
          </div>
          {ingestStatus === 'loading' && <InlineLoading description="Loading ingest jobs" />}
        </div>

        <DataTable
          rows={rows}
          headers={headers}
          isSortable
          render={({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id} {...getRowProps({ row })}>
                    {row.cells.map(cell => {
                      if (cell.info.header === 'status') {
                        return (
                          <TableCell key={cell.id}>
                            <span className={getStatusClass(cell.value)}>{displayStatus(cell.value)}</span>
                          </TableCell>
                        )
                      }
                      if (cell.info.header === 'createdAt' || cell.info.header === 'updatedAt') {
                        return (
                          <TableCell key={cell.id}>
                            {cell.value ? moment(cell.value).format('DD.MM.YYYY HH:mm:ss') : '—'}
                          </TableCell>
                        )
                      }
                      return <TableCell key={cell.id}>{cell.value}</TableCell>
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        />

        <Pagination
          totalItems={filteredJobs.length}
          pageSize={rowsPerPage}
          pageSizes={[5, 10, 20, 50]}
          page={currentPage}
          onChange={({ page, pageSize }) => {
            setCurrentPage(pageSize === rowsPerPage ? page : 1)
            setRowsPerPage(pageSize)
          }}
        />
      </TableContainer>

      <CreateIngestModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmitted={loadIngests}
      />
    </>
  )
}

export default Ingest
